import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ConfirmationNumber as TicketIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Define ticket type interface
export interface TicketType {
  id: string;
  name: string;
  description: string;
  price: number;
  capacity: number;
  isDefault?: boolean;
  ticketsSold?: number;
  revenue?: number;
  isVisible?: boolean;
  saleStartDate?: Date;
  saleEndDate?: Date;
}

interface TicketTypeManagerProps {
  ticketTypes: TicketType[];
  onChange: (tickets: TicketType[]) => void;
  maxCapacity?: number;
}

// Ticket form validation schema
const ticketValidationSchema = Yup.object({
  name: Yup.string().required('Ticket name is required'),
  description: Yup.string(),
  price: Yup.number().min(0, 'Price cannot be negative'),
  capacity: Yup.number().required('Capacity is required').min(1, 'Capacity must be at least 1')
});

const TicketTypeManager: React.FC<TicketTypeManagerProps> = ({ ticketTypes, onChange, maxCapacity }) => {
  const [isEditingTicket, setIsEditingTicket] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);
  
  // Simple state management for ticket form
  const [ticketName, setTicketName] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketPrice, setTicketPrice] = useState(0);
  const [ticketCapacity, setTicketCapacity] = useState(10);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  const validateTicketForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!ticketName.trim()) {
      errors.name = 'Ticket name is required';
    }
    
    if (ticketPrice < 0) {
      errors.price = 'Price cannot be negative';
    }
    
    if (!ticketCapacity || ticketCapacity < 1) {
      errors.capacity = 'Capacity must be at least 1';
    }
    
    if (maxCapacity && ticketCapacity > maxCapacity) {
      errors.capacity = `Capacity cannot exceed ${maxCapacity}`;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const resetTicketForm = () => {
    setTicketName('');
    setTicketDescription('');
    setTicketPrice(0);
    setTicketCapacity(10);
    setFormErrors({});
    setEditingTicketId(null);
    setIsEditingTicket(false);
  };
  
  const handleAddTicket = () => {
    resetTicketForm();
    setIsEditingTicket(true);
  };
  
  const handleEditTicket = (ticket: TicketType) => {
    setTicketName(ticket.name);
    setTicketDescription(ticket.description);
    setTicketPrice(ticket.price);
    setTicketCapacity(ticket.capacity);
    setEditingTicketId(ticket.id);
    setIsEditingTicket(true);
  };
  
  const handleDeleteTicket = (ticketId: string) => {
    const updatedTickets = ticketTypes.filter(t => t.id !== ticketId);
    onChange(updatedTickets);
  };
  
  const handleSubmitTicket = () => {
    if (!validateTicketForm()) {
      return;
    }
    
    if (editingTicketId) {
      // Update existing ticket
      const updatedTickets = ticketTypes.map(ticket => 
        ticket.id === editingTicketId 
          ? {
              ...ticket,
              name: ticketName,
              description: ticketDescription,
              price: ticketPrice,
              capacity: ticketCapacity
            } 
          : ticket
      );
      onChange(updatedTickets);
    } else {
      // Add new ticket
      const newTicket: TicketType = {
        id: Date.now().toString(),
        name: ticketName,
        description: ticketDescription,
        price: ticketPrice,
        capacity: ticketCapacity,
        isDefault: ticketTypes.length === 0
      };
      
      const newTickets = [...ticketTypes, newTicket];
      onChange(newTickets);
    }
    
    resetTicketForm();
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          <TicketIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Ticket Types
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleAddTicket}
          disabled={isEditingTicket}
        >
          Add Ticket Type
        </Button>
      </Box>
      
      {isEditingTicket ? (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {editingTicketId ? 'Edit Ticket' : 'Add New Ticket'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="ticketName"
                label="Ticket Name"
                placeholder="e.g., VIP, Early Bird, General Admission"
                value={ticketName}
                onChange={(e) => setTicketName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                autoFocus
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="ticketPrice"
                label="Price"
                type="number"
                value={ticketPrice}
                onChange={(e) => setTicketPrice(Number(e.target.value))}
                error={!!formErrors.price}
                helperText={formErrors.price}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="ticketDescription"
                label="Ticket Description"
                placeholder="Describe what's included with this ticket"
                multiline
                rows={2}
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="ticketCapacity"
                label="Available Quantity"
                type="number"
                value={ticketCapacity}
                onChange={(e) => setTicketCapacity(Number(e.target.value))}
                error={!!formErrors.capacity}
                helperText={formErrors.capacity}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PeopleIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={resetTicketForm}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmitTicket}
                >
                  {editingTicketId ? 'Update Ticket' : 'Add Ticket'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      ) : null}
      
      {ticketTypes.length > 0 ? (
        <List sx={{ mb: 2 }}>
          {ticketTypes.map((ticket) => (
            <ListItem 
              key={ticket.id}
              component={Paper}
              sx={{ mb: 1, p: 2 }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TicketIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1">{ticket.name}</Typography>
                    <Typography 
                      variant="subtitle1" 
                      color="primary" 
                      sx={{ ml: 'auto', fontWeight: 'bold' }}
                    >
                      {ticket.price > 0 ? `$${ticket.price.toFixed(2)}` : 'Free'}
                    </Typography>
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2">{ticket.description || 'No description provided'}</Typography>
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Sold: {ticket.ticketsSold || 0}/{ticket.capacity}
                      </Typography>
                      {ticket.price > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Revenue: ${((ticket.ticketsSold || 0) * ticket.price).toFixed(2)}
                        </Typography>
                      )}
                      <Typography 
                        variant="body2" 
                        color={ticket.isVisible === false ? "error.main" : "success.main"}
                      >
                        {ticket.isVisible === false ? "Hidden" : "Visible"}
                      </Typography>
                    </Box>
                  </>
                }
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  aria-label="edit"
                  onClick={() => handleEditTicket(ticket)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  edge="end" 
                  aria-label="delete"
                  onClick={() => handleDeleteTicket(ticket.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No ticket types defined yet. Add a ticket type to allow registrations.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddTicket}
            sx={{ mt: 2 }}
          >
            Add Ticket Type
          </Button>
        </Paper>
      )}
    </>
  );
};

export default TicketTypeManager; 